import { QRCodeSVG } from "qrcode.react";
import { X, Smartphone, Tv, Copy, Check, Wifi, Loader2, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { postCastLink, postCastCommand } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * TVCastOverlay — two-mode handoff dialog.
 *
 * Mode A · QR scan (default): The user scans the QR code with their TV /
 * tablet camera and picks up the current URL there — good for "open on
 * my other device".
 *
 * Mode B · Live cast (new): The TV is already running as a "receiver" at
 * /tv/cast and shows a 4-digit code. The user types the code here, we
 * link the phone + TV via /api/cast/link, and now every "Watch"
 * command on the phone plays instantly on the TV.
 *
 * Props
 * -----
 * open, onClose : standard modal controls
 * url           : QR target (falls back to window.location.href)
 * snapshotId    : optional — if provided, enables the "▶ Play on TV"
 *                 shortcut once paired
 */
const CAST_CODE_KEY = "tc:cast-code";

export default function TVCastOverlay({ open, onClose, url, snapshotId }) {
  const [copied, setCopied] = useState(false);
  const [castCode, setCastCode] = useState(() => {
    try { return localStorage.getItem(CAST_CODE_KEY) || ""; } catch { return ""; }
  });
  const [codeInput, setCodeInput] = useState("");
  const [linking, setLinking] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  // Autofocus the code input when overlay opens (unless we're already paired)
  useEffect(() => {
    if (open && !castCode && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, castCode]);

  if (!open) return null;
  const displayUrl = (url || "").replace(/^https?:\/\//, "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const linkCode = async () => {
    const code = codeInput.trim();
    if (!/^\d{4,5}$/.test(code)) {
      toast.error("Enter the 4-digit code shown on your TV");
      return;
    }
    setLinking(true);
    try {
      await postCastLink(code);
      setCastCode(code);
      try { localStorage.setItem(CAST_CODE_KEY, code); } catch { /* private mode */ }
      trackEvent("tv_cast_linked", { code });
      toast.success("Paired with your TV");
      // If we already have a snapshot in hand, cast it immediately
      if (snapshotId) {
        try {
          await postCastCommand({ code, snapshot_id: snapshotId, mode: "cinema" });
          trackEvent("tv_cast_command_sent", { snapshot_id: snapshotId });
          toast.success("Playing on your TV");
          setTimeout(onClose, 900);
        } catch { /* silent — user can retry */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pair — check the code and try again");
    } finally {
      setLinking(false);
    }
  };

  const castNow = async () => {
    if (!castCode || !snapshotId) return;
    setSending(true);
    try {
      await postCastCommand({ code: castCode, snapshot_id: snapshotId, mode: "cinema" });
      trackEvent("tv_cast_command_sent", { snapshot_id: snapshotId, code: castCode });
      toast.success("Playing on your TV");
      setTimeout(onClose, 700);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("expired") || detail.toLowerCase().includes("not found")) {
        // Session died — force re-pair
        setCastCode("");
        try { localStorage.removeItem(CAST_CODE_KEY); } catch { /* private mode */ }
        toast.error("TV pairing expired — get a fresh code from your TV and pair again");
      } else {
        toast.error(detail || "Couldn't reach the TV");
      }
    } finally {
      setSending(false);
    }
  };

  const unpair = () => {
    setCastCode("");
    try { localStorage.removeItem(CAST_CODE_KEY); } catch { /* private mode */ }
    trackEvent("tv_cast_unpair");
    toast.info("Unpaired from TV");
  };

  const overlayJsx = (
    <div
      className="fixed inset-0 z-[9500] bg-black/85 flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm"
      onClick={onClose}
      dir="ltr"
      style={{ direction: "ltr" }}
      data-testid="tv-cast-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        dir="ltr"
        style={{ direction: "ltr", textAlign: "left" }}
        className="relative w-full max-w-md bg-paper text-ink border-2 border-ink shadow-[8px_8px_0_#1A1C20] max-h-[92vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 border-2 border-ink flex items-center justify-center hover:bg-ink hover:text-paper bg-paper z-10"
          aria-label="Close"
          data-testid="tv-cast-close-btn"
        >
          <X size={16} />
        </button>

        <div className="border-b-2 border-ink px-6 py-3 flex items-center gap-2 bg-paper-alt">
          <Tv size={16} className="text-press-red" />
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs">
            Watch on another screen
          </div>
        </div>

        {/* PAIRED — show cast controls + option to unpair */}
        {castCode ? (
          <div className="p-6 sm:p-8 text-center">
            <div className="inline-flex items-center gap-2 mb-3 font-mono uppercase tracking-[0.3em] text-[10px] text-emerald-700">
              <Wifi size={12} /> Paired with TV · {castCode}
            </div>
            <div className="font-headline uppercase font-black text-2xl sm:text-3xl leading-tight tracking-tighter">
              Cast to your TV.
            </div>
            <p className="mt-2 font-body italic text-sm text-ink-muted">
              {snapshotId ? "Tap Play — this episode starts on your paired TV in a second." : "Pick any date, tap Watch, and it plays on your TV."}
            </p>

            {snapshotId ? (
              <button
                onClick={castNow}
                disabled={sending}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-ink px-4 py-4 font-headline uppercase tracking-widest text-sm sm:text-base hover:bg-ink disabled:opacity-70"
                data-testid="tv-cast-play-btn"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                {sending ? "Sending to TV…" : "Play on TV now"}
              </button>
            ) : null}

            <div className="mt-5 border-t border-dashed border-ink/30 pt-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
              <span className="text-ink-muted">Switch TVs or unpair?</span>
              <button
                onClick={unpair}
                className="border-2 border-ink px-3 py-1.5 hover:bg-ink hover:text-paper"
                data-testid="tv-cast-unpair-btn"
              >
                Unpair
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-8 text-center">
            {/* Path A: pair with a 4-digit code from a live receiver */}
            <div className="font-headline uppercase font-black text-2xl sm:text-3xl leading-tight tracking-tighter">
              Pair with your TV.
            </div>
            <p className="mt-2 font-body italic text-sm text-ink-muted">
              On your TV, open <span className="font-mono not-italic">timecapsule24.tv/tv/cast</span> — enter the 4-digit code it shows.
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); linkCode(); }}
              className="mt-5 flex items-stretch gap-2"
              data-testid="tv-cast-pair-form"
            >
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{4,5}"
                maxLength={5}
                autoComplete="off"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="0000"
                data-testid="tv-cast-code-input"
                className="flex-1 min-w-0 border-2 border-ink px-3 py-3 font-headline text-2xl sm:text-3xl tracking-[0.25em] text-center bg-paper-surface focus:outline-none focus:bg-white"
              />
              <button
                type="submit"
                disabled={linking || codeInput.length < 4}
                className="flex-shrink-0 bg-ink text-paper border-2 border-ink px-4 py-3 font-mono uppercase tracking-widest text-xs hover:bg-press-red disabled:opacity-50 min-w-[80px]"
                data-testid="tv-cast-pair-submit"
              >
                {linking ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Pair"}
              </button>
            </form>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted text-left">
              No TV code yet? On your TV, visit <span className="text-press-red">timecapsule24.tv/tv/cast</span> first.
            </div>

            {/* Path B: fallback — the classic QR handoff */}
            <div className="my-6 border-t-2 border-dashed border-ink/40 relative">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-paper px-3 font-mono uppercase tracking-widest text-[10px] text-ink-muted">
                or scan &amp; open on any device
              </span>
            </div>

            <div className="inline-block p-4 bg-white border-2 border-ink" data-testid="tv-cast-qr">
              <QRCodeSVG value={url} size={180} level="M" includeMargin={false} />
            </div>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Smartphone size={14} className="text-ink-muted" />
              <div className="font-mono text-[11px] sm:text-xs break-all">{displayUrl}</div>
            </div>
            <button
              onClick={copy}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-ink px-4 py-2.5 font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-ink hover:text-paper"
              data-testid="tv-cast-copy-btn"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied to clipboard" : "Copy link"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Portal into document.body so parent RTL/dir doesn't affect layout
  return typeof document !== "undefined"
    ? createPortal(overlayJsx, document.body)
    : overlayJsx;
}
